import {useQuery} from "@tanstack/react-query";
import {getSubjects} from "api/subject.api.ts";
import {filterAllowedClasses} from "conts/allowedClasses";
import {useUpdateTeacher} from "context/UpdateTeacherContext.jsx";
import {useToast} from "hooks/index.js";
import {useEffect, useState} from "react";
import styled from "styled-components";
import {ButtonPrimary} from "ui/base/Button/ButtonStyled.js";
import {EditAvatar} from "ui/base/editAvatar/EditAvatar.jsx";
import {HelperText} from "ui/base/helperText/HelperText.jsx";
import {InputSignUp} from "ui/base/InputSignUp/InputSignUp.jsx";
import {RadioButton} from "ui/base/radioButton/RadioButton.jsx";
import {RoundedRadioButton} from "ui/base/roundedRadioButton/roundedRadioButton.jsx";
import {hideScrollBar} from "ui/base/scrollbarStyle/scrollbarStyle.js";
import {TextArea} from "ui/base/textArea/TextArea.jsx";
import {TextAreaInput} from "ui/base/textArea/TextAreaInput.jsx";
import {SearchIcon, ZipCodeIcon} from "ui/icons";
import {IntegerValidation, numberOnly, TEXT_LENGTH_LIMIT, TEXT_LENGTH_MIN_LIMIT, validateFirstStep,} from "utils";
import {AddPlaceRequest} from "./addPlaceRequest.jsx";
import {UnlistModal} from "./UnlistModal.jsx";

export const Listing = ({onSubmit, loading, handleErrors}) => {
    const {formData, changeFormData, setFormData} = useUpdateTeacher();
    const [isValidating, setIsValidating] = useState(false);

    const [showAdditionalInputs, setShowAdditionalInputs] = useState({
        whatExpectMyClassOnline: false,
        whatParticipantsTakeClass: false,
    });

    const {data: subjectData} = useQuery({
        queryKey: ["subjects"],
        queryFn: getSubjects,
    });

    useEffect(() => {
        setShowAdditionalInputs({
            whatExpectMyClassOnline: formData?.expected_students_online || false,
            whatParticipantsTakeClass: formData?.student_need_to_have_online || false,
        });
    }, [
        formData?.expected_students_online,
        formData?.student_need_to_have_online,
    ]);


    const onImageUpload = (imageUri) => {
        changeFormData("profile_pic", {
            name: "profilePic",
            type: "image/jpg",
            uri: imageUri,
            uploaded: false,
        });
    };

    const addButtonClick = (title) => {
        const arr = studentLevel;
        if (arr.indexOf(title) === -1) {
            arr.push(title);
        } else {
            arr.splice(arr.indexOf(title), 1);
        }

        changeFormData("level_of_student", arr.join(","));
    };

    const resetOnlineFields = () => {
        setFormData((prevData) => ({
            ...prevData,
            expected_students_online: "",
            student_need_to_have_online: "",
            // locations_of_class: "",
        }));

        setShowAdditionalInputs({
            whatExpectMyClassOnline: false,
            whatParticipantsTakeClass: false,
        });
    };

    const studentLevel = formData?.level_of_student?.split(",");

    const filteredSubjects = filterAllowedClasses(subjectData);
    
    const subjects =
        filteredSubjects?.map((subject) => ({
            value: subject.id,
            label: subject.title,
        })) || [];

    const subject =
        subjects?.find(
            (subject) => subject.value === formData.subject_want_to_teach
        ) || null;

    const onClick = () => {
        setIsValidating(true);

        const objectToValidate = {
            profile_pic: formData.profile_pic,
            subject_want_to_teach: formData.subject_want_to_teach,
            subject_sub_category: formData.subject_sub_category,
            zip_code: formData.zip_code,
            city: formData.city,
            state: formData.state,
            // neighborhood: formData.neighborhood,
            experience_on_subject: formData.experience_on_subject,
            level_of_student: formData.level_of_student,
            student_need_to_have: formData.student_need_to_have,
            expected_students: formData.expected_students,
        };

        if (formData?.online_type === "offline") {
            objectToValidate.price_per_private_class =
                formData?.price_per_private_class;
            objectToValidate.neighborhood = formData.neighborhood;
        }

        if (formData.online_type != "offline") {
            objectToValidate.price_per_online_class =
                formData?.price_per_online_class;
        }

        if (formData.online_type != "online") {
            objectToValidate.locations_of_class = formData.locations_of_class;
        }

        if (showAdditionalInputs.whatExpectMyClassOnline) {
            objectToValidate.expected_students_online =
                formData.expected_students_online;
        }

        if (showAdditionalInputs.whatParticipantsTakeClass) {
            objectToValidate.student_need_to_have_online =
                formData.student_need_to_have_online;
        }

        if (formData?.online_type === "online") {
            setFormData((prevData) => ({
                ...prevData,
                locations_of_class: "",
                neighborhood: ""
            }));
        }

        let errors = validateFirstStep(objectToValidate, true);

        if (Object.keys(errors).length > 0) {
            if (
                errors["price_per_private_class"] == "" ||
                errors["price_per_group_class"] == "" ||
                errors["price_per_online_class"] == ""
            ) {
                handleErrors("PRICES")
                useToast("error", "Price must be filled");
            } else if (errors?.zip_code || errors?.city || errors?.state) {
                useToast("error", errors?.zip_code || errors?.city || errors?.state);
            } else if (errors["profile_pic"] !== undefined) {
                useToast("error", "Upload profile picture");
            } else {
                useToast("error", "Fill all the fields");
            }
        } else {
            //
            onSubmit();
        }
    };

    return (
        <Container>
            <HorizontalContainer>
                <AvatarContainer>
                    <EditAvatar
                        avatar={formData.profile_pic.uri}
                        onImageUpload={onImageUpload}
                        variant
                    />
                    <HelperText> Provide a photo that clearly shows your face</HelperText>
                </AvatarContainer>
                {formData && <UnlistModal formData={formData}/>}
            </HorizontalContainer>
            {validateFirstStep(formData, isValidating)["profile_pic"] !=
                undefined && (
                    <HelperText color="pink" size="small">
                        {validateFirstStep(formData, isValidating)["profile_pic"]}
                    </HelperText>
                )}

            <InputContainer>
                <SelectContainer column={2}>
                    <div className="column">

                        <HelperText>{"What are you teaching?"}</HelperText>

                        <InputSignUp
                            disabled
                            id='subject_want_to_teach'
                            value={subject?.label}
                            placeholder='Search'
                            icon={<SearchIcon/>}
                        />
                        {/* <AddSubjectRequest /> */}
                    </div>
                    <div>
                        <InputSignUp
                            type="number"
                            inputmode="numeric"
                            icon={<ZipCodeIcon/>}
                            onInput={numberOnly}
                            label="In what Zip Code are you located?"
                            value={formData.zip_code}
                            setValue={(input) => {
                                const text = IntegerValidation(input);
                                if (text.length <= 5) {
                                    return changeFormData(
                                        "zip_code",
                                        isNaN(parseInt(text, 10)) ? "" : text
                                    );
                                }
                            }}
                            placeholder="Zip Code"
                            isError={
                                validateFirstStep(formData, isValidating)["zip_code"] !==
                                undefined ||
                                validateFirstStep(formData, isValidating)["city"] !==
                                undefined ||
                                validateFirstStep(formData, isValidating)["state"] !== undefined
                            }
                            errorText={
                                validateFirstStep(formData, isValidating)["zip_code"] ||
                                validateFirstStep(formData, isValidating)["city"] ||
                                validateFirstStep(formData, isValidating)["state"]
                            }
                            maxLength={5}
                        />
                    </div>
                </SelectContainer>

                <TextAreaInput
                    label={
                        `What will your class focus on?\ne.g., If your class is about cooking, here you can write something like "Pasta Making"`
                    }
                    placeholder="Be specific and concise about what you are teaching."
                    value={formData.subject_sub_category}
                    setValue={(text) => changeFormData("subject_sub_category", text)}
                    isError={
                        validateFirstStep(formData, isValidating)[
                            "subject_sub_category"
                            ] !== undefined
                    }
                    errorText={
                        validateFirstStep(formData, isValidating)["subject_sub_category"]
                    }
                    maxLength={20}
                    showLimit
                />

                <TextArea
                    label="Tell user about your experience on the subject"
                    placeholder="Describe your experience, including how long have you been practicing or teaching. Mention any certifications or accomplishments, if applicable."
                    value={formData.experience_on_subject}
                    setValue={(text) => changeFormData("experience_on_subject", text)}
                    maxLength={TEXT_LENGTH_LIMIT["experience_on_subject"]}
                    minLength={TEXT_LENGTH_MIN_LIMIT["experience_on_subject"]}
                    isError={
                        validateFirstStep(formData, isValidating)["experience_on_subject"]
                    }
                    errorText={
                        validateFirstStep(formData, isValidating)["experience_on_subject"]
                    }
                />

                <div
                    style={{display: "grid", gap: "1rem"}}
                    className="teaching-label"
                >
                    <HelperText>What level of students are you teaching?</HelperText>
                    <SelectButtonsContainer>
                        <RadioButton
                            onClick={() => addButtonClick("beginner")}
                            selected={studentLevel?.indexOf("beginner") !== -1}
                        >
                            Beginner
                        </RadioButton>
                        <RadioButton
                            onClick={() => addButtonClick("experienced")}
                            selected={studentLevel?.indexOf("experienced") !== -1}
                        >
                            Experienced
                        </RadioButton>
                        <RadioButton
                            onClick={() => addButtonClick("expert")}
                            selected={studentLevel?.indexOf("expert") !== -1}
                        >
                            Expert
                        </RadioButton>
                    </SelectButtonsContainer>
                    {validateFirstStep(formData, isValidating)["level_of_student"] !==
                        undefined && (
                            <HelperText color="pink">
                                {validateFirstStep(formData, isValidating)["level_of_student"]}
                            </HelperText>
                        )}
                </div>

                <div>
                    <HelperText> Do you teach online, in-person or both?</HelperText>

                    <SelectButtonsContainer>
                        <RoundedRadioButton
                            label={"Online"}
                            id={"online"}
                            isSelected={formData?.online_type == "online"}
                            onSelected={(id) => {
                                changeFormData("online_type", id);
                                resetOnlineFields();
                            }}
                        />
                        <RoundedRadioButton
                            label={"In-Person"}
                            id={"offline"}
                            isSelected={formData?.online_type == "offline"}
                            onSelected={(id) => {
                                changeFormData("online_type", id);
                                resetOnlineFields();
                            }}
                        />
                        <RoundedRadioButton
                            label={"Online & In-Person"}
                            id={"both"}
                            isSelected={formData?.online_type == "both"}
                            onSelected={(id) => {
                                changeFormData("online_type", id);
                                resetOnlineFields();
                            }}
                        />
                    </SelectButtonsContainer>
                </div>

                <SelectPosContainer hide={formData?.online_type === "online"}>
                    <InputSignUp
                        icon={<ZipCodeIcon/>}
                        label="What is the class neighborhood?"
                        value={formData.neighborhood}
                        setValue={(value) => {
                            changeFormData("neighborhood", value);
                        }}
                        placeholder="Neighborhood"
                        maxLength={17}
                        isError={
                            validateFirstStep(formData, isValidating)["neighborhood"] !==
                            undefined
                        }
                        errorText={
                            validateFirstStep(formData, isValidating)["neighborhood"]
                        }
                    />

                    <TextArea
                        label="Where do you teach the class?"
                        height={"64px"}
                        placeholder="Describe the location, such as your own apartment, a private studio, a public park , etc."
                        value={formData.locations_of_class}
                        // maxLength={TEXT_LENGTH_LIMIT["locations_of_class"]}
                        // minLength={TEXT_LENGTH_MIN_LIMIT["locations_of_class"]}
                        setValue={(text) => changeFormData("locations_of_class", text)}
                        isError={
                            validateFirstStep(formData, isValidating)["locations_of_class"]
                        }
                        errorText={
                            validateFirstStep(formData, isValidating)["locations_of_class"]
                        }
                        centerText
                    />
                </SelectPosContainer>

                <TextArea
                    label="What should students expect in your class?"
                    placeholder="Provide detailed information on what students can expect from start to finish while participating in your class."
                    value={formData.expected_students}
                    maxLength={TEXT_LENGTH_LIMIT["expected_students"]}
                    minLength={TEXT_LENGTH_MIN_LIMIT["expected_students"]}
                    setValue={(text) => changeFormData("expected_students", text)}
                    isError={
                        validateFirstStep(formData, isValidating)["expected_students"]
                    }
                    errorText={
                        validateFirstStep(formData, isValidating)["expected_students"]
                    }
                />

                {showAdditionalInputs.whatExpectMyClassOnline ? (
                    <TextArea
                        label="What should students expect in your class? (online)"
                        placeholder="Provide detailed information..."
                        value={formData?.expected_students_online}
                        maxLength={TEXT_LENGTH_LIMIT["expected_students_online"]}
                        minLength={TEXT_LENGTH_MIN_LIMIT["expected_students_online"]}
                        setValue={(text) =>
                            changeFormData("expected_students_online", text)
                        }
                        isError={
                            validateFirstStep(formData, isValidating)[
                                "expected_students_online"
                                ]
                        }
                        errorText={
                            validateFirstStep(formData, isValidating)[
                                "expected_students_online"
                                ]
                        }
                    />
                ) : null}

                {formData?.online_type === "both" &&
                !showAdditionalInputs.whatExpectMyClassOnline ? (
                    <AddPlaceRequest
                        onPress={() =>
                            setShowAdditionalInputs((prev) => ({
                                ...prev,
                                whatExpectMyClassOnline: true,
                            }))
                        }
                    />
                ) : null}

                <TextArea
                    label="What do participants need to join your class?"
                    placeholder="List any requirements such as wardrobe, shoes, equipment or membership. Fewer requirements from the user can lead to more bookings. Mention any equipment you'll provide."
                    value={formData.student_need_to_have}
                    maxLength={TEXT_LENGTH_LIMIT["student_need_to_have"]}
                    minLength={TEXT_LENGTH_MIN_LIMIT["student_need_to_have"]}
                    setValue={(text) => changeFormData("student_need_to_have", text)}
                    isError={
                        validateFirstStep(formData, isValidating)[
                            "student_need_to_have"
                            ] !== undefined
                    }
                    errorText={
                        validateFirstStep(formData, isValidating)["student_need_to_have"]
                    }

                />

                {showAdditionalInputs.whatParticipantsTakeClass ? (
                    <TextArea
                        label="What do participants need to join your class? (online)"
                        placeholder="List any requirements such as wardrobe, shoes, equipment or membership. Fewer requirements from the user can lead to more bookings. Also, mention any equipment you’ll provide."
                        value={formData.student_need_to_have_online}
                        maxLength={TEXT_LENGTH_LIMIT["student_need_to_have_online"]}
                        minLength={TEXT_LENGTH_MIN_LIMIT["student_need_to_have_online"]}
                        setValue={(text) =>
                            changeFormData("student_need_to_have_online", text)
                        }
                        isError={
                            validateFirstStep(formData, isValidating)[
                                "student_need_to_have_online"
                                ] !== undefined
                        }
                        errorText={
                            validateFirstStep(formData, isValidating)[
                                "student_need_to_have_online"
                                ]
                        }
                    />
                ) : null}
                {formData?.online_type === "both" &&
                !showAdditionalInputs.whatParticipantsTakeClass ? (
                    <AddPlaceRequest
                        onPress={() =>
                            setShowAdditionalInputs((prev) => ({
                                ...prev,
                                whatParticipantsTakeClass: true,
                            }))
                        }
                    />
                ) : null}
            </InputContainer>
            <div style={{width: "100%", display: "flex"}}>
                <ButtonPrimary onClick={onClick} disabled={loading}>
                    Save Changes
                </ButtonPrimary>
            </div>
        </Container>
    );
};

const Container = styled.div`
    width: 100%;
    margin-inline: auto;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow-y: auto;
    gap: 40px;
    padding-bottom: 2rem;
    font-family: var(--font-secondary);
    ${hideScrollBar}
`;

const AvatarContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    gap: 0.6rem;
`;
const HorizontalContainer = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
`;

const InputContainer = styled.div`
    display: grid;
    gap: 1.5rem;
`;

const SelectContainer = styled.div`
    display: grid;
    gap: 1rem;

    & .column {
        display: grid;
        gap: 1rem;
    }

    @media (min-width: 768px) {
        grid-template-columns: ${({column}) => `repeat(${column}, 1fr)`};
    }
`;

const SelectPosContainer = styled.div`
    display: ${({hide}) => hide ? "none" : "grid"};
    gap: 1rem;
    grid-template-columns: repeat(2, 1fr);

    & .column {
        display: grid;
        gap: 1rem;
    }
`;

export const SelectButtonsContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    @media (min-width: 768px) {
        grid-template-columns: repeat(3, 1fr);
    }
`;
